import { memo, useCallback, useState } from 'react'

import { ControlPointDuplicate } from '@mui/icons-material'
import { ListItemIcon, Typography, Divider, MenuItem, Skeleton } from '@mui/material'
import { DrmProviderInsert } from '@nativewaves/nw-cloud-sdk/content'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { ActionMenu, AnimatedButton } from '@shared/components/ContextMenu'
import { MenuItemCopyId, MenuItemDelete } from '@shared/components/MaterialUIEnhancements/Menu/MenuItem'
import { useDrmProviderCreateMutation, useDrmProviderDeleteMutation } from 'hooks/mutations/content'
import { drmProvidersOverviewRoute } from 'pages/NWPlatform/DrmProviders'
import { drmProviderIndexRoute } from 'pages/NWPlatform/DrmProviders/DrmProvider'
import { drmProviderQueryKeys } from 'services/queryKeys'

const ActionsMenuRaw = () => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const { drmProviderId } = drmProviderIndexRoute.useParams()
  const navigate = useNavigate({ from: drmProviderIndexRoute.fullPath })

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>()

  const drmProviderQuery = useQuery(drmProviderQueryKeys.detail({ drmProviderId }))
  const { data: drmProvider } = drmProviderQuery

  const { mutate: createDrmProvider, ...drmProviderCreateMutation } = useDrmProviderCreateMutation()
  const { mutate: deleteDrmProvider } = useDrmProviderDeleteMutation()

  const handleDeletion = useCallback(() => {
    if (!drmProvider) return

    setAnchorEl(undefined)

    deleteDrmProvider(
      { drmProviderId: drmProvider.id },
      {
        onSuccess: () => {
          enqueueSnackbar(t('domain:DrmProvider.nameDelete', { name: drmProvider.name }), { variant: 'success' })

          setTimeout(() => {
            navigate({ to: drmProvidersOverviewRoute.to, replace: true })
          }, 300)
        },
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [deleteDrmProvider, drmProvider, enqueueSnackbar, navigate, t])

  const handleDuplicateDrmProvider = useCallback(() => {
    if (!drmProvider) return

    setAnchorEl(undefined)

    createDrmProvider(
      { drmProviderInsert: { ...drmProvider, name: `${drmProvider.name} (${t('duplicate')})` } as DrmProviderInsert },
      {
        onSuccess: () => {
          enqueueSnackbar(t('duplicationSuccess'), { variant: 'success' })
        },
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [drmProvider, createDrmProvider, enqueueSnackbar, t])

  return (
    <>
      <AnimatedButton
        open={!!anchorEl}
        IconButtonProps={{
          onClick: (e) => setAnchorEl(e.currentTarget),
          color: 'inherit',
        }}
      />
      <ActionMenu
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(undefined)
        }}
        title={drmProvider?.name}
        entity={t('entity:content.drmProvider', { count: 1 })}
      >
        <MenuItem onClick={handleDuplicateDrmProvider}>
          <ListItemIcon>
            <ControlPointDuplicate />
          </ListItemIcon>
          <Typography sx={{ width: '100%' }}>
            {drmProviderCreateMutation.isPending ? <Skeleton variant="text" /> : t('duplicate')}
          </Typography>
        </MenuItem>

        <MenuItemCopyId onSuccess={() => setAnchorEl(undefined)} id={drmProviderId} />

        <Divider sx={{ my: 1 }} component="li" />

        <MenuItemDelete
          staticLabel={t('domain:DrmProvider.deleteDrmProvider', { count: 1 })}
          onConfirm={handleDeletion}
        />
      </ActionMenu>
    </>
  )
}

export const ActionsMenu = memo(ActionsMenuRaw)
